import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { FormScenario } from 'src/models/form.model';

import { AnyValue, Maybe, RecordObject } from 'src/models/general.model';
import { logBeta, logIncorrectData } from 'src/tools/log.tools';
import { hasKey } from 'src/tools/object.tools';
import { useDebouncedFn } from './useDebouncedFn';
import { isNil } from 'ramda';
import { convertToForm } from 'src/tools/form.tools';
import { CONFIG } from 'src/config';
import { isEmail } from 'src/validators';
import { ErrorsObject } from 'src/models/error.model';

const NON_GENERIC_TYPES = ['File', 'File[]', 'email', 'password', 'csv'];

const URL_REGEXP =
  /^((http|https):\/\/)?(www\.)?([^.])([A-Za-z0-9_\-.]+)\.?\/?([A-Za-z]{2,})([A-Za-z0-9_])?\/?([A-Za-z0-9_\-?=&]+)?$/;

const EMPTY_ERRORS = {};

function isGenericType(type: string) {
  return !NON_GENERIC_TYPES.includes(type);
}

export function useForm<T = Record<string, AnyValue>>(
  scenario?: Maybe<FormScenario>,
  initialState?: T,
  initialErrors?: ErrorsObject,
) {
  const defaultForm = useMemo(() => {
    let newForm = initialState ?? ({} as Record<string, AnyValue>);

    if (scenario) {
      for (const key of Object.keys(scenario)) {
        if (hasKey('defaultValue', scenario[key]) && !hasKey(key, newForm))
          newForm = { ...newForm, [key]: scenario[key].defaultValue };
      }
    }

    return newForm as Record<string, AnyValue>;
  }, [initialState, scenario]);

  const defaultInvalidValues = useMemo(() => {
    const newInvalidValues: RecordObject<AnyValue> = {};

    if (initialErrors && scenario)
      Object.keys(scenario).forEach(k => {
        if (initialErrors[k]) newInvalidValues[k] = defaultForm[k];
      });

    return newInvalidValues;
  }, [defaultForm, initialErrors, scenario]);

  const [form, setForm] = useState<Record<string, AnyValue>>(defaultForm);

  const invalidValues = useRef<Record<string, AnyValue>>(defaultInvalidValues);

  const [isValid, setIsValid] = useState(false);

  const [errors, setErrors] = useState<ErrorsObject>({});

  const [serverErrors, setServerErrors] = useState<ErrorsObject>(initialErrors ?? EMPTY_ERRORS);

  const [isReadyForValidation, setIsReadyForValidation] = useState(false);

  const isFile = useCallback((key: string) => form[key] instanceof File, [form]);

  const isFileArray = useCallback(
    (key: string) => {
      if (!Array.isArray(form[key])) return false;

      let result = true;

      for (const file of form[key]) {
        result = file instanceof File;
      }

      return result;
    },
    [form],
  );

  const checkIsHidden = useCallback(
    (key: string) => {
      const script = scenario?.[key];

      return (
        !script ||
        (script.isHiddenFromKey && !!form[script.isHiddenFromKey]) ||
        (script.isVisibleFromKey && form[script.isVisibleFromKey] === false)
      );
    },
    [form, scenario],
  );

  const getRequiredKeys = useCallback(() => {
    if (!scenario) return [];

    let result: string[] = [];

    for (const key of Object.keys(scenario)) {
      const { isRequired, isDisabledFromKey } = scenario[key];

      const isHidden = checkIsHidden(key);

      if (isRequired && !isHidden && !form[isDisabledFromKey ?? '']) result = [...result, key];
    }

    return result;
  }, [checkIsHidden, form, scenario]);

  const validateForm = useCallback(() => {
    if (!scenario) return;

    const requiredKeys = getRequiredKeys();

    const formKeys = Object.keys(form);

    const currentErrors = { _general: initialErrors?._general } as ErrorsObject;

    Object.keys(invalidValues.current).forEach(k => {
      if (invalidValues.current[k] !== form[k]) {
        delete invalidValues.current[k];
      } else currentErrors[k] = serverErrors[k];
    });

    // form is valid if there is only 1 key in errors (_general)
    let result = Object.keys(currentErrors).length === 1;

    for (const key of requiredKeys) {
      if (typeof form[key] === 'undefined' || form[key] === '') {
        const fieldName = 'This field';

        result = false;

        currentErrors[key] = `${fieldName} is required.`;

        logIncorrectData('validateForm', `Required value for [${key}] is missing.`);
      }
    }

    let passwordKey;

    // check value type
    for (const key of formKeys) {
      // if dynamic private field (is not defined in scenario) OR field is required and missing
      if (!scenario[key] || currentErrors[key]) {
        continue;
      }

      const fieldName = scenario[key].label ?? 'Field';
      const typeToBe = scenario[key].type;
      const isDisabled = scenario[key].isDisabled;

      if (typeToBe) {
        if (typeToBe === 'File' && !isFile(key)) {
          result = false;

          currentErrors[key] = `${fieldName} should have type File, not ${typeof form[key]}.`;

          logIncorrectData('validateForm', `Data ${key}: ${form[key]} should have type File, not ${typeof form[key]}`);
        } else if (typeToBe === 'File[]' && !isFileArray(key)) {
          result = false;

          currentErrors[key] = `${fieldName} should have type File, not ${typeof form[key]}.`;

          logIncorrectData('validateForm', `Data ${key}: ${form[key]} should have type File, not ${typeof form[key]}`);
        } else if (typeToBe === 'password') {
          // confirm password field validation
          if (passwordKey && form[passwordKey] !== form[key]) {
            result = false;

            currentErrors[key] = `Both passwords must match.`;

            logIncorrectData('validateForm', `Data ${key}: password and confirm password does not match`);
          }

          passwordKey = key;
        } else if (typeToBe === 'tel') {
          // TODO: implement phone validation
        } else if (typeToBe === 'email') {
          const item = form[key].trim();

          if (!isEmail(item)) {
            result = false;

            currentErrors[key] = 'Please enter a valid email address.';

            logIncorrectData(
              'validateForm',
              `Data ${key}: ${form[key]} has issues: missing '@': ${!item.includes('@')}, missing '.': ${!item.includes(
                '.',
              )}, extra spaces: ${item.includes(' ')}.`,
            );
          }
        } else if (typeToBe === 'url') {
          const item = form[key].trim();

          if (!URL_REGEXP.test(item)) {
            result = false;

            currentErrors[key] = 'Please enter a valid website.';

            logIncorrectData('validateForm', `Data ${key}: ${form[key]} is invalid url`);
          }
        } else if (isGenericType(typeToBe) && typeof form[key] !== typeToBe && !isDisabled) {
          result = false;

          currentErrors[key] = `${fieldName} should have type ${typeToBe}, not ${typeof form[key]}.`;

          logIncorrectData(
            'validateForm',
            `Data ${key}: ${form[key]} should have type ${typeToBe}, not ${typeof form[key]}`,
          );
        }
      }

      if (scenario[key].showAs === 'checkboxGroup' && scenario[key].isRequired) {
        if (!form[key].length) {
          result = false;

          currentErrors[key] = `You must select at least one option.`;

          logIncorrectData('validateForm', `Required value for [${key}] is missing.`);
        }
      }

      if (hasKey('min', scenario[key])) {
        const minToBe = scenario[key].min!;

        if (typeof form[key] === 'number' && form[key] < minToBe) {
          result = false;

          currentErrors[key] = `${fieldName} cannot be less than ${minToBe}.`;

          logIncorrectData(
            'validateForm',
            `Data ${key}: ${
              scenario[key].type === 'password' ? '***' : form[key]
            } should have min length ${minToBe}, not ${form[key].toString().length}`,
          );
        } else if (form[key].length < minToBe) {
          result = false;

          currentErrors[key] = `Must be at least ${minToBe} characters.`;

          logIncorrectData(
            'validateForm',
            `Data ${key}: ${
              scenario[key].type === 'password' ? '***' : form[key]
            } should have min length ${minToBe}, not ${form[key].length}`,
          );
        }
      }

      if (hasKey('max', scenario[key])) {
        const maxToBe = scenario[key].max!;

        if (typeof form[key] === 'number' && form[key] > maxToBe) {
          result = false;

          currentErrors[key] = `${fieldName} cannot be greater than ${maxToBe}.`;

          logIncorrectData(
            'validateForm',
            `Data ${key}: ${
              scenario[key].type === 'password' ? '***' : form[key]
            } should have max length ${maxToBe}, not ${form[key]}`,
          );
        } else if (form[key].length > maxToBe) {
          result = false;

          currentErrors[key] = `Must be no longer than ${maxToBe} characters.`;

          logIncorrectData(
            'validateForm',
            `Data ${key}: ${
              scenario[key].type === 'password' ? '***' : form[key]
            } should have max length ${maxToBe}, not ${form[key].length}`,
          );
        }
      }
    }

    // if negative result, but was set to positive
    if (!result && isValid) {
      setIsValid(false);
    }

    // if positive result
    else if (result && !isValid) {
      setIsValid(true);
    }

    setErrors(currentErrors);
  }, [form, getRequiredKeys, initialErrors?._general, isFile, isFileArray, isValid, scenario, serverErrors]);

  const debouncedValidateFn = useDebouncedFn(
    validateForm,
    scenario?._init?.validationDebounceDelay ?? CONFIG.validationDebounceDelay,
  );

  const setDefaults = useCallback(() => setForm(defaultForm), [defaultForm]);

  // reset form if initial value is changed
  useEffect(() => setDefaults(), [setDefaults]);

  useEffect(() => {
    if (scenario && !isReadyForValidation) {
      setIsReadyForValidation(true);
    }
  }, [isReadyForValidation, scenario]);

  useEffect(() => {
    logBeta('form', form);

    if (isReadyForValidation) {
      debouncedValidateFn();
    }
  }, [debouncedValidateFn, form, isReadyForValidation]);

  const publicForm = useMemo(() => {
    const result: RecordObject<AnyValue> = {};

    if (!scenario) return result;

    for (const key of Object.keys(scenario)) {
      const isPrivate = scenario[key]?.isPrivate || key.startsWith('_');

      const isHidden = checkIsHidden(key);

      if (hasKey(key, scenario) && !isPrivate && !isHidden && !isNil(form[key])) {
        result[key] = form[key];
      }
    }

    return result;
  }, [form, scenario, checkIsHidden]);

  const formData = useMemo(
    () => convertToForm(publicForm, scenario?._init?.formKey),
    [publicForm, scenario?._init?.formKey],
  );

  const handleSetServerErrors = useCallback(
    (newServerErrors: ErrorsObject = {}) => {
      const newInvalidValues: Record<string, AnyValue> = {};

      Object.keys(newServerErrors).forEach(k => {
        newInvalidValues[k] = form[k];
      });

      invalidValues.current = newInvalidValues;

      setServerErrors(newServerErrors);
    },
    [form],
  );

  return { form, setForm, publicForm, formData, setDefaults, isValid, errors, setServerErrors: handleSetServerErrors };
}
